/* eslint-disable @typescript-eslint/no-unused-vars */
import CountdownV2 from "components/CountdownV2";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { IShopPurchaseOfferElite } from "contexts/WebStoreProvider/types";
import { WsDialogs } from "interfaces";
import React, { useEffect } from "react";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useImagePath } from "hooks";
import DebugCopyIdButton from "debug/components/DebugCopyIdButton";
import { CrownIconV2 } from "utils/svgIcons";
//import { eliteDealsAssets } from "assets/cdn";
import {
	CustomButton,
	Container,
	CountdownIconStyles,
	CountdownLabelStyles,
	CrossOutLine,
	CrossOutValue,
	DataContainer,
	Message,
	Footer,
	Header,
	LoyaltyPoints,
	LoyaltyPointsRow,
	Multiplier,
	NumberValue,
	Reward,
	RewardIcon,
	RewardsContainer,
	Text,
	SubTitle,
	Title,
	CountdownStyles,
	CountdownStylesLandscape,
	CountdownTimerStyles,
	RewardWrapper,
	RewardsBackground
} from "./styles";

import useRewardsById from "hooks/useRewardsById";
import { InfoBanner } from "./InfoBanner";
import { useMediaQuery } from "@mui/material";
import { getFlexBasis } from "./helpers/getFlexBasis";
import { sortBundleRewards } from "helpers/rewardSort";
import { getTimeLeft } from "utils/time";
import { formatCompactNumber } from "utils/number-formatter";

interface Props {
	className?: string;
	offer: IShopPurchaseOfferElite;
	refetch: () => void;
}

export const Box: React.FC<Props> = ({ className, offer, refetch }) => {
	const { open } = usePopUpsV2();
	const { openDialog } = useDialogModalRedux();
	const { getImage } = useImagePath("cdn");
	//const backgroundImage = getImage(eliteDealsAssets.boxBackground);
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const {
		authToken,
		handleRefresh,
		openLoginToContinueDialog,
		postUserMetadata,
		suppressOrderConfirmation,
		wsStoreBuy,
		nowTime
	} = useWebStoreContext();

	const isPortrait = useMediaQuery("(orientation: portrait)");
	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty messaging.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );

	useEffect(() => {
		const timeLeft = getTimeLeft(nowTime, offer.dayCycleResetTime, "seconds");
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "offer_rendered",
			event_type: "single_item",
			action: "offer_rendered",
			source: "vip_shop",
			offer_id: offer?.id,
			reward_id: offer?.rewardId,
			price_in_cents:
				typeof offer?.price === "number"
					? Math.round(offer.price * 100)
					: undefined,
			value_int: offer?.loyaltyPointReward,
			value_int2: offer?.dayCyclePurchases,
			value_int3: offer?.dayCyclePurchaseLimit,
			time_left: timeLeft
		});
	}, [offer]);

	const rewards = useRewardsById(offer.rewardId);
	const crossoutRewards = useRewardsById(offer?.crossoutRewardId ?? "");

	const availablePurchases =
		(offer.dayCyclePurchaseLimit ?? 0) - (offer.dayCyclePurchases ?? 0);
	const multiplier =
		Number(offer?.loyaltyPointCrossout) > 0
			? (
					Number(offer?.loyaltyPointReward) /
					Number(offer?.loyaltyPointCrossout)
			  )
					.toFixed(1)
					.replace(".0", "")
			: "1";
	const hasDecimalMultiplier = multiplier.includes(".");
	const validateToken = () => {
		if (!authToken?.value) {
			openLoginToContinueDialog();
			return false;
		}
		return true;
	};

	const onClickBuy = async () => {
		if (!validateToken()) {
			return;
		}

		if (offer.upsellOfferConfig) {
			open({
				variant: `bundle-up::${offer.id}`,
				id: `bundle-up::${offer.id}`,
				replaceIfExists: true
			});
			return;
		}

		const invoice = await wsStoreBuy({ offerId: offer.id });
		if (invoice) {
			openDialog(WsDialogs.XSOLLA_PURCHASE, {
				xsollaToken: invoice.xsollaToken,
				xsollaHost: invoice.xsollaHost,
				invoice,
				section: "elite_deals",
				source: "boxes",
				pricePoint: offer.price,
				priority: offer.priority,
				suppressOrderConfirmation,
				postUserMetadata,
				onPurchaseSuccess: () => {
					handleRefresh();
				}
			});
		}
	};

	//const arrayTest = [1, 2, 3, 4, 5, 6, 7, 8];

	return (
		<Container className={className} color="#FFF">
			<CountdownV2
				startTime={nowTime}
				endTime={offer?.dayCycleResetTime || ""}
				text="Resets in:"
				style={isPortrait ? CountdownStyles : CountdownStylesLandscape}
				iconStyle={CountdownIconStyles}
				labelStyle={CountdownLabelStyles}
				timerStyle={CountdownTimerStyles}
				onEnd={() => {
					refetch();
				}}
				status={offer.id}
			/>
			<Header $backgroundImage={offer?.backgroundImageUrl || ""}>
				<Title>
					<Text className="title">Elite</Text>
				</Title>
				<Title>
					<Text className="main-title">{offer.description}</Text>
				</Title>
				{offer.bannerText && (
					<InfoBanner
						text={offer.bannerText}
						text2={offer.bannerText}
						color={offer.eliteBadgeColorHex}
						borderColor={offer.eliteBadgeBorderColorHex}
					/>
				)}
			</Header>

			<DataContainer>
				<SubTitle>
					<Text className="subtitle">You get:</Text>
				</SubTitle>
				<RewardsBackground>
					<RewardsContainer className={className}>
						{sortBundleRewards(rewards).map((reward) => {
							return (
								<Reward
									key={reward.id}
									style={{
										flexBasis:
											getFlexBasis({
												length: rewards.length,
												isPortrait
											}) + "%"
									}}
								>
									<RewardWrapper>
										<RewardIcon src={reward?.imageUrl} />
										<NumberValue style={{ paintOrder: "stroke fill" }}>
											<Text className="number-value">
												{formatCompactNumber(reward?.description)}
											</Text>
										</NumberValue>
										{crossoutRewards.length > 0 && (
											<CrossOutValue className="reward">
												<span style={{ fontSize: "1em", position: "relative" }}>
													<CrossOutLine className="reward" />
													{formatCompactNumber(
														crossoutRewards.find((cr) => cr.id === reward.id)
															?.description || reward?.description
													)}
												</span>
											</CrossOutValue>
										)}
									</RewardWrapper>
								</Reward>
							);
						})}

						{/* {arrayTest.map(({}, key) => {
						return (
							<Reward
								key={key}
								style={{
									flexBasis:
										getFlexBasis({
											length: arrayTest.length,
											isPortrait
										}) + "%"
								}}
							>
								<RewardWrapper>
									<RewardIcon
										src={
											key % 2 === 0
												? "https://cdn.solitairetripeaks.com/WebStore/reward_images/coin.png"
												: "https://cdn-a1.tripeaksapp.com/WebStore/reward_images/wild.png"
										}
									/>
									<NumberValue style={{ paintOrder: "stroke fill" }}>
										<Text className="number-value">{200}</Text>
									</NumberValue>
									{false && (
										<CrossOutValue className="reward">
											<span style={{ fontSize: "1em" }}>
												<CrossOutLine className="reward" />
												<>200</>
											</span>
										</CrossOutValue>
									)}
								</RewardWrapper>
							</Reward>
						);
					})} */}
					</RewardsContainer>
				</RewardsBackground>
			</DataContainer>
			<Footer>
				<LoyaltyPoints
					className={offer?.loyaltyPointCrossout ? "crossout" : ""}
				>
					{offer?.loyaltyPointCrossout && (
						<CrossOutValue>
							<LoyaltyPointsRow>
								<CrossOutLine />
								<span>Earn</span>
								<CrownIconV2 style={{ width: "1.2em", height: "1.2em" }} />
								<span>
									{Number(offer?.loyaltyPointCrossout).toLocaleString("en-US")}
								</span>
							</LoyaltyPointsRow>
						</CrossOutValue>
					)}

					<LoyaltyPointsRow>
						<span>Earn </span>
						<CrownIconV2 style={{ width: "1.2em", height: "1.2em" }} />
						<span>{offer?.loyaltyPointReward.toLocaleString("en-US")}</span>
					</LoyaltyPointsRow>
					{offer?.loyaltyPointCrossout && (
						<Multiplier className={hasDecimalMultiplier ? "has-decimals" : ""}>
							<div>
								<span>x</span>
								{multiplier}
							</div>
						</Multiplier>
					)}
				</LoyaltyPoints>
				<CustomButton
					className={className}
					variant="contained"
					color="success"
					size="large"
					disabled={availablePurchases === 0}
					onClick={onClickBuy}
				>
					{offer.price} USD
				</CustomButton>
			</Footer>
			<Message>
				<Text
					className="purchase-limit"
					style={{ color: availablePurchases === 0 ? "#FF0000" : "#7E7E7E" }}
				>
					{availablePurchases}/{offer.dayCyclePurchaseLimit} available per week
				</Text>
			</Message>
			{/*
				{isPoisActive && (
					<Message className="pois-bounty">
						<Text className="pois-bounty">Contribute to Poi’s Bounty</Text>
					</Message>
				)}
			*/}
			<DebugCopyIdButton id={offer.id} style={{ fontSize: "0.9em" }} />
		</Container>
	);
};
